import React, { useState, useEffect, useCallback } from 'react';
import { X, Bell, Check, Trash2, Loader2, Package, Sparkles, Calendar, ChevronRight, MoreVertical } from 'lucide-react';
import { getNotifications, markAsRead, markAllAsRead, deleteNotification, deleteAllNotifications } from '../api/notification.api';
import cacheService from '../utils/cacheService';

const NOTIFICATION_CACHE_TTL = 60 * 1000; // 1 minute

const Inbox = ({ onClose, showNotification, setActiveView, onNotificationFabricClick }) => {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'unread'
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);

  useEffect(() => {
    loadNotifications();
  }, [activeTab, page]);

  const loadNotifications = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const cacheKey = `notifications_${activeTab}_${page}`;
      const cached = cacheService.get(cacheKey);

      if (cached) {
        setNotifications(cached.notifications);
        setHasMore(cached.hasMore);
        setIsLoading(false);
        return;
      }

      const response = await getNotifications(token, page, 20, activeTab === 'unread');
      const data = response.data.data;

      setNotifications(data.notifications);
      setHasMore(data.pagination.hasNextPage);

      // Cache the data
      cacheService.set(cacheKey, {
        notifications: data.notifications,
        hasMore: data.pagination.hasNextPage
      }, NOTIFICATION_CACHE_TTL);
    } catch (error) {
      console.error('Error loading notifications:', error);
      showNotification?.('Failed to load notifications', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await markAsRead(notificationId, token);

      // Update local state
      setNotifications(prev =>
        prev.map(n =>
          n._id === notificationId ? { ...n, isRead: true, readAt: new Date() } : n
        )
      );

      // Clear cache
      cacheService.delete(`notifications_${activeTab}_${page}`);
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setIsMarkingAllRead(true);
      const token = localStorage.getItem('token');
      await markAllAsRead(token);

      // Update local state
      setNotifications(prev =>
        prev.map(n => ({ ...n, isRead: true, readAt: new Date() }))
      );

      // Clear all notification caches
      ['all', 'unread'].forEach(tab => {
        for (let i = 1; i <= page; i++) {
          cacheService.delete(`notifications_${tab}_${i}`);
        }
      });

      showNotification?.('All notifications marked as read', 'success');
    } catch (error) {
      console.error('Error marking all as read:', error);
      showNotification?.('Failed to mark all as read', 'error');
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await deleteNotification(notificationId, token);

      // Update local state
      setNotifications(prev => prev.filter(n => n._id !== notificationId));

      // Clear cache
      cacheService.delete(`notifications_${activeTab}_${page}`);

      showNotification?.('Notification deleted', 'success');
    } catch (error) {
      console.error('Error deleting notification:', error);
      showNotification?.('Failed to delete notification', 'error');
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if not already
    if (!notification.isRead) {
      handleMarkAsRead(notification._id);
    }

    const productId = notification.relatedData?.productId;
    const productIdStr = productId ? (typeof productId === 'string' ? productId : productId.toString?.() || String(productId)) : null;
    const isFabricNotification = ['new_public_fabric', 'fabric_made_public', 'new_shade_card'].includes(notification.type);

    // For fabric notifications, open fabric detail page
    if (productIdStr && isFabricNotification && onNotificationFabricClick) {
      onClose();
      onNotificationFabricClick(productIdStr);
      return;
    }

    // Fallback: Handle navigation based on actionUrl
    if (notification.relatedData?.actionUrl) {
      onClose();
      if (notification.relatedData.actionUrl.includes('marketplace')) {
        setActiveView?.('fabricmarketplace');
      }
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_public_fabric':
      case 'fabric_made_public':
        return <Package size={20} className="text-blue-500" />;
      case 'new_shade_card':
        return <Sparkles size={20} className="text-purple-500" />;
      default:
        return <Bell size={20} className="text-gray-500" />;
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInSeconds = Math.floor((now - notificationDate) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;

    return notificationDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-2xl mx-auto mt-16 sm:mt-20 bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[calc(100vh-4rem)] flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                <Bell size={20} className="text-white" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Notifications</h2>
                {unreadCount > 0 && (
                  <p className="text-xs text-gray-500">{unreadCount} unread</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} className="text-gray-600" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-4">
            <button
              onClick={() => { setActiveTab('all'); setPage(1); }}
              className={`pb-2 px-1 font-semibold text-sm transition-all relative ${
                activeTab === 'all' ? 'text-blue-600' : 'text-gray-500'
              }`}
            >
              All
              {activeTab === 'all' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
              )}
            </button>
            <button
              onClick={() => { setActiveTab('unread'); setPage(1); }}
              className={`pb-2 px-1 font-semibold text-sm transition-all relative ${
                activeTab === 'unread' ? 'text-blue-600' : 'text-gray-500'
              }`}
            >
              Unread
              {activeTab === 'unread' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
              )}
              {unreadCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-blue-500 text-white text-[10px] font-bold rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>
          </div>

          {/* Actions */}
          {unreadCount > 0 && (
            <div className="mt-3 flex gap-2">
              <button
                onClick={handleMarkAllAsRead}
                disabled={isMarkingAllRead}
                className="text-xs font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-50 flex items-center gap-1"
              >
                {isMarkingAllRead ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Marking...
                  </>
                ) : (
                  <>
                    <Check size={14} />
                    Mark all as read
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && page === 1 ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={32} className="text-gray-400 animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Bell size={28} className="text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No notifications</h3>
              <p className="text-sm text-gray-500 text-center max-w-sm">
                {activeTab === 'unread'
                  ? "You're all caught up! No unread notifications."
                  : "You'll see notifications here when creators you follow post new content."}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => (
                <div
                  key={notification._id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`px-4 sm:px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                    !notification.isRead ? 'bg-blue-50/50' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      !notification.isRead ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      {notification.relatedData?.creatorAvatar ? (
                        <img
                          src={notification.relatedData.creatorAvatar}
                          alt={notification.relatedData.creatorName}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        getNotificationIcon(notification.type)
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-gray-900">{notification.title}</h4>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(notification._id);
                          }}
                          className="p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                        >
                          <Trash2 size={14} className="text-gray-500" />
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{notification.message}</p>
                      
                      {/* Fabric Image Preview */}
                      {notification.relatedData?.fabricImage && (
                        <div className="mt-2 mb-2">
                          <img
                            src={notification.relatedData.fabricImage}
                            alt={notification.relatedData.fabricName}
                            className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                          />
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          {formatTimeAgo(notification.createdAt)}
                        </span>
                        {!notification.isRead && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Load More */}
          {hasMore && !isLoading && (
            <div className="p-4 text-center">
              <button
                onClick={() => setPage(prev => prev + 1)}
                className="px-4 py-2 text-sm font-semibold text-blue-600 hover:text-blue-700"
              >
                Load more
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Inbox;
